import { useState } from 'react';
import { Edit2, Check, X, Plus, Minus } from 'lucide-react';
import { usePlans, useUpdatePlan } from '@/hooks/usePlans';
import { cn } from '@/lib/utils';
import FeatureTable from '@/components/FeatureTable';

export default function Plans() {
  const { data: plans, isLoading } = usePlans();
  const updatePlan = useUpdatePlan();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    base_price: 0,
    per_location_price: 0,
    per_register_price: 0,
    included_locations: 0,
    included_registers: 0,
  });

  // Simulator state
  const [locations, setLocations] = useState(1);
  const [registers, setRegisters] = useState(1);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annually'>('annually');

  const startEditing = (plan: typeof plans extends (infer T)[] | undefined ? T : never) => {
    if (!plan) return;
    setEditingId(plan.id);
    setEditForm({
      base_price: plan.base_price,
      per_location_price: plan.per_location_price,
      per_register_price: plan.per_register_price,
      included_locations: plan.included_locations,
      included_registers: plan.included_registers,
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
  };

  const saveEditing = async () => {
    if (!editingId) return;

    try {
      await updatePlan.mutateAsync({
        id: editingId,
        updates: editForm,
      });
      setEditingId(null);
    } catch (error) {
      console.error('Failed to update plan:', error);
    }
  };

  // Calculate cost for a given plan based on simulator inputs
  const calculatePlanCost = (plan: NonNullable<typeof plans>[number]) => {
    const extraLocations = Math.max(0, locations - plan.included_locations);
    const extraRegisters = Math.max(0, registers - plan.included_registers);

    // Use annual prices if available, otherwise calculate from monthly (10 months = annual price)

    if (billingPeriod === 'annually') {
      const baseCost = (plan.annual_base_price || plan.base_price * 10) / 12;
      const locationCost = extraLocations * ((plan.annual_per_location_price || plan.per_location_price * 10) / 12);
      const registerCost = extraRegisters * ((plan.annual_per_register_price || plan.per_register_price * 10) / 12);
      return {
        baseCost: Math.round(baseCost),
        locationCost: Math.round(locationCost),
        registerCost: Math.round(registerCost),
        total: Math.round(baseCost + locationCost + registerCost)
      };
    } else {
      const baseCost = plan.base_price;
      const locationCost = extraLocations * plan.per_location_price;
      const registerCost = extraRegisters * plan.per_register_price;
      return { baseCost, locationCost, registerCost, total: baseCost + locationCost + registerCost };
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Subscription Plans</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Manage pricing plans and simulate costs
        </p>
      </div>

      {/* Cost Simulator Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          {/* Number of Locations */}
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
              Number of locations
            </label>
            <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
              <button
                onClick={() => setLocations(Math.max(1, locations - 1))}
                className="px-3 py-2 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors border-r border-gray-300 dark:border-gray-600"
                aria-label="Decrease locations"
              >
                <Minus className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              </button>
              <input
                type="number"
                value={locations}
                onChange={(e) => setLocations(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-16 text-center py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none"
                min="1"
              />
              <button
                onClick={() => setLocations(locations + 1)}
                className="px-3 py-2 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors border-l border-gray-300 dark:border-gray-600"
                aria-label="Increase locations"
              >
                <Plus className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
          </div>

          {/* Number of Registers */}
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
              Number of registers
            </label>
            <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
              <button
                onClick={() => setRegisters(Math.max(1, registers - 1))}
                className="px-3 py-2 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors border-r border-gray-300 dark:border-gray-600"
                aria-label="Decrease registers"
              >
                <Minus className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              </button>
              <input
                type="number"
                value={registers}
                onChange={(e) => setRegisters(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-16 text-center py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none"
                min="1"
              />
              <button
                onClick={() => setRegisters(registers + 1)}
                className="px-3 py-2 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors border-l border-gray-300 dark:border-gray-600"
                aria-label="Increase registers"
              >
                <Plus className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
          </div>

          {/* Billing Period Toggle */}
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 p-1">
              <button
                onClick={() => setBillingPeriod('annually')}
                className={cn(
                  'px-4 py-1.5 text-sm font-medium rounded-md transition-colors',
                  billingPeriod === 'annually'
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                )}
              >
                Annually
              </button>
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={cn(
                  'px-4 py-1.5 text-sm font-medium rounded-md transition-colors',
                  billingPeriod === 'monthly'
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                )}
              >
                Monthly
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Plan Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans?.map((plan) => {
          const isEditing = editingId === plan.id;
          const costs = calculatePlanCost(plan);

          return (
            <div
              key={plan.id}
              className={cn(
                'bg-white dark:bg-gray-800 rounded-xl border-2 transition-colors flex flex-col',
                plan.name === 'Pro'
                  ? 'border-blue-500'
                  : 'border-gray-200 dark:border-gray-700'
              )}
            >
              {plan.name === 'Pro' && (
                <div className="bg-blue-500 text-white text-center text-xs font-medium py-1">
                  Most Popular
                </div>
              )}

              <div className="p-6 flex-1">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {plan.name}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {plan.description}
                    </p>
                  </div>
                  {!isEditing ? (
                    <button
                      onClick={() => startEditing(plan)}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                  ) : (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={saveEditing}
                        disabled={updatePlan.isPending}
                        className="p-2 text-green-600 hover:text-green-700"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                      <button
                        onClick={cancelEditing}
                        className="p-2 text-red-600 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Simulated Total Price */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">
                      {costs.total}
                    </span>
                    <span className="text-lg text-gray-500 dark:text-gray-400">USD</span>
                    <span className="text-gray-500 dark:text-gray-400">/mo</span>
                  </div>
                </div>

                {/* Cost Breakdown */}
                <div className="space-y-2 text-sm border-t border-gray-200 dark:border-gray-700 pt-4">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                    Per month:
                  </p>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Base plan cost</span>
                    <span className="font-medium text-gray-900 dark:text-white">{costs.baseCost} USD</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Extra locations</span>
                    <span className="font-medium text-gray-900 dark:text-white">{costs.locationCost} USD</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Additional registers</span>
                    <span className="font-medium text-gray-900 dark:text-white">{costs.registerCost} USD</span>
                  </div>
                </div>

                {/* Included - Collapsed during simulation */}
                {isEditing && (
                  <>
                    <div className="space-y-3 mb-6 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Included
                      </h3>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Locations</span>
                        <input
                          type="number"
                          value={editForm.included_locations}
                          onChange={(e) => setEditForm({ ...editForm, included_locations: Number(e.target.value) })}
                          className="w-16 text-right bg-transparent border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Registers</span>
                        <input
                          type="number"
                          value={editForm.included_registers}
                          onChange={(e) => setEditForm({ ...editForm, included_registers: Number(e.target.value) })}
                          className="w-16 text-right bg-transparent border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>

                    {/* Base Price Edit */}
                    <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Base Pricing
                      </h3>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Base Price</span>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500">$</span>
                          <input
                            type="number"
                            value={editForm.base_price}
                            onChange={(e) => setEditForm({ ...editForm, base_price: Number(e.target.value) })}
                            className="w-20 text-right bg-transparent border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Per Location</span>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500">$</span>
                          <input
                            type="number"
                            value={editForm.per_location_price}
                            onChange={(e) => setEditForm({ ...editForm, per_location_price: Number(e.target.value) })}
                            className="w-16 text-right bg-transparent border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Per Register</span>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500">$</span>
                          <input
                            type="number"
                            value={editForm.per_register_price}
                            onChange={(e) => setEditForm({ ...editForm, per_register_price: Number(e.target.value) })}
                            className="w-16 text-right bg-transparent border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Stripe IDs */}
              <div className="px-6 pb-4 pt-2 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Stripe Product: {plan.stripe_product_id || 'Not linked'}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Feature Comparison Table */}
      <FeatureTable />
    </div>
  );
}















